import {
  fetchExperienceCategories,
  fetchExperienceListFacets,
} from 'actions/ExperienceActions'
import { EXP_CATEGORY_LUX_PLUS_ID } from 'constants/experience'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject, groupBy, sortBy, sum } from 'lib/array/arrayUtils'
import getObjectKey from 'lib/object/getObjectKey'
import { mapTravelStylesToTargetAudience } from 'lib/experiences/experienceUtils'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { useEffect, useMemo } from 'react'
import useExperiencesSearchFilters from './useExperiencesSearchFilters'

export interface CategoryData {
  parent: App.ExperienceItemCategory;
  children: Array<App.ExperienceItemCategory>;
  count: number;
}

function useMostPopulatedSearchExperienceFacets(place?: App.Place): {
  primaryCategories: Array<CategoryData>;
  categories: Array<App.ExperienceCategoryFacet>;
  collections: Array<App.ExperienceCollectionFacet>;
  priceRange: App.ExperienceFacets['price'];
  leExclusiveCount: number;
  freeCancellationCount: number;
  instantConfirmationCount: number;
  giftingAvailableCount: number;
  luxPlusMemberPriceCount: number;
  ratingCounts: Array<App.ExperienceRatingCountFacet>;
  targetAudienceCounts: Array<App.ExperienceTargetAudienceCountFacet>;
  timeSlotCounts: Array<App.ExperienceTimeSlotCountFacet>;
  fetching: boolean;
} {
  const dispatch = useAppDispatch()
  const categories = useAppSelector(
    (state) => state.experience.experienceCategories,
  )
  const searchFacets = useAppSelector(
    (state) => state.experience.experienceListFacets,
  )
  const isLuxPlusEnabled = useAppSelector((state) =>
    getIsEnabledLuxPlusDiscountedExperiences(state),
  )
  const experiencesCollectionsEnabled = useOptimizelyExperiment(
    OptimizelyExperiments.experiencesCollections,
  )
  const { searchFilters } = useExperiencesSearchFilters()

  const facetFilters = useMemo(() => {
    const targetAudience = searchFilters.travelStyles?.length
      ? mapTravelStylesToTargetAudience(searchFilters.travelStyles).join(',')
      : undefined
    return {
      availableFrom: searchFilters.checkIn,
      availableTo: searchFilters.checkOut,
      collections: searchFilters.collections?.join(','),
      priceGte: searchFilters.priceGte,
      priceLte: searchFilters.priceLte,
      leExclusive: searchFilters.leExclusive,
      luxPlusExclusive: searchFilters.luxPlusFeatures?.join(','),
      ratingRange: searchFilters.minRating !== undefined ? String(searchFilters.minRating) : undefined,
      targetAudience,
      timeSlots: searchFilters.timeSlots?.join(',') || undefined,
    }
  }, [searchFilters])

  const facetKey = useMemo(
    () => place ? getObjectKey({ placeId: place.id, ...facetFilters }) : '',
    [place, facetFilters],
  )

  useEffect(() => {
    if (!place) {
      return
    }

    dispatch(fetchExperienceListFacets(place.id, facetFilters))
    dispatch(fetchExperienceCategories())
  }, [place, facetFilters, dispatch])

  const categoriesFacets = useMemo<Array<App.ExperienceCategoryFacet>>(() => {
    if (!place) {
      return []
    }

    return searchFacets[facetKey]?.facets?.category ?? []
  }, [place, facetKey, searchFacets])

  const primaryCategories = useMemo<Array<CategoryData>>(() => {
    if (categoriesFacets.length == 0) {
      return []
    }

    // prepare reference data for access in calculations below
    const childToParentCategories = groupBy(
      categories.filter((cat) => cat.parentId),
      (cat) => cat.parentId,
    )
    const categoryFacetsById = arrayToObject(
      categoriesFacets,
      (cat) => cat.categoryId,
    )

    // we show a tile for each 'parent' category
    const topLevelCategories = categories.filter(
      (cat) => cat.level === 'parent',
    )
    // new search facets doesn't return a count at the parent level, so we have to manually
    // add up all child facets to get an accurate count
    const topLevelCategoryFacets = topLevelCategories.map((cat) => ({
      cat,
      enabled: cat.id !== EXP_CATEGORY_LUX_PLUS_ID || isLuxPlusEnabled,
      count: sum(
        childToParentCategories.get(cat.id) ?? [],
        (childCat) => categoryFacetsById[childCat.id]?.count ?? 0,
      ),
    }))

    const sortedCategories = sortBy(
      topLevelCategoryFacets,
      (val) => val.count,
      'desc',
    )

    return sortedCategories
      .filter((fac) => fac.count !== 0 && fac.enabled)
      .map((fac) => ({
        parent: fac.cat,
        children: childToParentCategories.get(fac.cat.id) ?? [],
        count: fac.count,
      }))
  }, [categories, categoriesFacets, isLuxPlusEnabled])

  const collections = useMemo<Array<App.ExperienceCollectionFacet>>(() => {
    if (!place || !experiencesCollectionsEnabled) {
      return []
    }

    return searchFacets[facetKey]?.facets?.collection ?? []
  }, [place, facetKey, searchFacets, experiencesCollectionsEnabled])

  const priceRange = useMemo<App.ExperienceFacets['price']>(() => {
    if (!place) {
      return { min: 0, max: 0 }
    }

    return searchFacets[facetKey]?.facets?.price ?? { min: 0, max: 0 }
  }, [facetKey, searchFacets, place])

  // If we don't have a place, we can't fetch facets (API is keyed by place id),
  // so avoid showing an "infinite" loading state.
  const fetching = !!place && (searchFacets[facetKey]?.fetching ?? false)

  const leExclusiveCount = useMemo(() => {
    return place ? searchFacets[facetKey]?.facets?.leExclusiveCount ?? 0 : 0
  }, [place, facetKey, searchFacets])

  const ratingCounts = useMemo(() => {
    return place ? searchFacets[facetKey]?.facets?.ratingCounts ?? [] : []
  }, [place, facetKey, searchFacets])

  const targetAudienceCounts = useMemo(() => {
    return place
      ? searchFacets[facetKey]?.facets?.targetAudienceCounts ?? []
      : []
  }, [place, facetKey, searchFacets])

  const freeCancellationCount = useMemo(() => {
    return place
      ? searchFacets[facetKey]?.facets?.freeCancellationCount ?? 0
      : 0
  }, [place, facetKey, searchFacets])

  const instantConfirmationCount = useMemo(() => {
    return place
      ? searchFacets[facetKey]?.facets?.instantConfirmationCount ?? 0
      : 0
  }, [place, facetKey, searchFacets])

  const giftingAvailableCount = useMemo(() => {
    return place
      ? searchFacets[facetKey]?.facets?.giftingAvailableCount ?? 0
      : 0
  }, [place, facetKey, searchFacets])

  const luxPlusMemberPriceCount = useMemo(() => {
    return place
      ? searchFacets[facetKey]?.facets?.luxPlusMemberPriceCount ?? 0
      : 0
  }, [place, facetKey, searchFacets])

  const timeSlotCounts = useMemo(() => {
    return place ? searchFacets[facetKey]?.facets?.timeSlotCounts ?? [] : []
  }, [place, facetKey, searchFacets])

  return {
    primaryCategories,
    categories: categoriesFacets,
    collections,
    priceRange,
    leExclusiveCount,
    freeCancellationCount,
    instantConfirmationCount,
    giftingAvailableCount,
    luxPlusMemberPriceCount,
    ratingCounts,
    targetAudienceCounts,
    timeSlotCounts,
    fetching,
  }
}

export default useMostPopulatedSearchExperienceFacets
